import { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useGlobalForm } from '@workspace/core-ts/contexts';
import { useQueryGetTarif } from '@workspace/core-ts/api';
import { Button, FieldSlider } from '@workspace/core-ts/components';
import { useSource, useAvenant } from '@workspace/core-ts/hooks';
import type { CommissionConfig } from './tarif-unified/types';

export type { CommissionConfig };

/**
 * Composant Commission pour les formulaires AMO
 * Permet aux courtiers et conseillers de configurer leur taux de commission et honoraires
 */
export function Commission({ config }: { config?: CommissionConfig }) {
    const { showTauxApporteur = false, tauxApporteurLocked = false } = config || {};
    const methods = useFormContext();
    const { meta } = useGlobalForm();
    const { isCourtier, isConseiller, isAprilMoto } = useSource();
    const isAvenant = useAvenant();

    const [showSuccess, setShowSuccess] = useState(false);
    const queryTarif = useQueryGetTarif(meta.token, methods.getValues(), false);

    const TarifPolice = methods.watch('Tarif.Police');
    const Police = methods.watch('DemandeTarif.Police');

    useEffect(() => {
        if (queryTarif.isSuccess && !queryTarif.isFetching && queryTarif.isFetchedAfterMount) {
            setShowSuccess(true);
        }

        setTimeout(() => {
            setShowSuccess(false);
        }, 5000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryTarif.isSuccess, queryTarif.isFetching]);

    // Convertit les strings en numbers et copie les valeurs par défaut depuis TarifPolice vers DemandeTarif.Police
    useEffect(() => {
        if (!TarifPolice) return;

        // Convertir les max en numbers
        if (typeof TarifPolice.HonorairesMaxi === 'string') {
            methods.setValue('Tarif.Police.HonorairesMaxi', parseInt(TarifPolice.HonorairesMaxi));
        }
        if (showTauxApporteur && typeof TarifPolice.TauxApporteur1Maxi === 'string') {
            methods.setValue('Tarif.Police.TauxApporteur1Maxi', parseInt(TarifPolice.TauxApporteur1Maxi));
        }

        // Copier les valeurs par défaut depuis TarifPolice vers DemandeTarif.Police si non définies
        const honorairesValue = Police?.Honoraires ?? TarifPolice.Honoraires;
        const honorairesNum = typeof honorairesValue === 'string' ? parseInt(honorairesValue) : honorairesValue;
        if (Police?.Honoraires === undefined || typeof Police?.Honoraires === 'string') {
            methods.setValue('DemandeTarif.Police.Honoraires', honorairesNum ?? 0);
        }

        if (showTauxApporteur) {
            const tauxValue = Police?.TauxApporteur1 ?? TarifPolice.TauxApporteur1;
            const tauxNum = typeof tauxValue === 'string' ? parseInt(tauxValue) : tauxValue;
            if (Police?.TauxApporteur1 === undefined || typeof Police?.TauxApporteur1 === 'string') {
                methods.setValue('DemandeTarif.Police.TauxApporteur1', tauxNum ?? 0);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [TarifPolice, Police, showTauxApporteur]);

    // Attendre que les valeurs soient converties en numbers avant d'afficher
    if (
        typeof TarifPolice?.HonorairesMaxi === 'string' ||
        Police?.Honoraires === undefined ||
        typeof Police?.Honoraires === 'string' ||
        (showTauxApporteur && (typeof TarifPolice?.TauxApporteur1Maxi === 'string' || Police?.TauxApporteur1 === undefined || typeof Police?.TauxApporteur1 === 'string')) ||
        isAvenant
    ) {
        return null;
    }

    if (!TarifPolice?.HonorairesMaxi) {
        return null;
    }

    // Condition d'affichage : courtier OU conseiller non April Moto
    if (!(isCourtier || (isConseiller && !isAprilMoto))) {
        return null;
    }

    const resetTarif = async () => {
        const result = await queryTarif.refetch();
        methods.setValue('Tarif', result.data?.data.Tarif);
    };

    return (
        <div className="w-full lg:max-w-lg mx-auto">
            <div className="bg-white border border-border w-full lg:rounded-md padding-root py-8">
                <h2 className="text-lg sm:text-xl font-semibold text-secondary mb-4 sm:mb-6">Votre commission</h2>
                <div>
                    {showTauxApporteur && TarifPolice.TauxApporteur1Maxi > 0 && (
                        <FieldSlider
                            defaultValue={0}
                            label="Votre taux de commission"
                            max={TarifPolice.TauxApporteur1Maxi}
                            min={0}
                            name="DemandeTarif.Police.TauxApporteur1"
                            disabled={tauxApporteurLocked}
                        />
                    )}
                    <FieldSlider
                        defaultValue={0}
                        label="Frais de dossier"
                        max={TarifPolice.HonorairesMaxi}
                        min={0}
                        name="DemandeTarif.Police.Honoraires"
                        unit="€"
                    />
                </div>

                <Button
                    arrow={false}
                    className="w-full mt-4"
                    loading={queryTarif.isFetching}
                    onClick={() => resetTarif()}
                    size="xs"
                    variant="outline"
                >
                    Recalculer le tarif
                </Button>

                {showSuccess && <p className="text-sm font-medium text-primary mt-4">Tarif calculé avec succès.</p>}
            </div>
        </div>
    );
}
